import { gettext } from '@eventbrite/i18n';
import { formatQuery } from 'url-lib';
import { translateServerErrors, sdkRequest, fetchJSON } from '@eventbrite/http';
import {
    SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK,
    SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE,
} from '@eventbrite/social-login-facebook';
import { fetchAuthentication } from './utils';

import {
    NOT_EMAIL_ASSOCIATED,
    PASSWORD_INCORRECT,
    ENTER_VALID_EMAIL,
} from '../constants/messages';

const SOCIAL_ACTION_URL = '/ajax/social_action/';
const SET_PASSWORD_URL = '/users/ajax/resetpassword/';

const STANDARD_SERVER_ERROR = gettext(
    'There was an issue with the server, try again',
);

export const LOGIN_ERROR_SPEC = {
    password: {
        INVALID: gettext('Incorrect password'),
        REQUIRED: gettext('Password is required'),
    },

    email: {
        UNKNOWN: gettext('This email is not recognized'),
        REQUIRED: gettext('Email is required'),
    },
    captcha: {
        REQUIRED: gettext('This field is required'),
        INCORRECT_SOLUTION: gettext('Provided solution incorrect'),
    },
    __all__: {
        LOCKED_ACCOUNT: gettext('This account has been locked'),
        SUSPENDED_ACCOUNT: gettext('This account has been suspended'),
        CREDENTIALS_MUST_CHANGE: gettext('This password has expired'),
        FORBIDDEN: gettext('Login failed'),
        LOGIN_GATED: STANDARD_SERVER_ERROR,
    },
};

export const LOGIN_ERROR_SPEC_V2 = {
    password: {
        INVALID: PASSWORD_INCORRECT,
        REQUIRED: gettext('Password is required'),
    },

    email: {
        UNKNOWN: NOT_EMAIL_ASSOCIATED,
        REQUIRED: gettext('Email is required'),
    },
    captcha: {
        REQUIRED: gettext('This field is required'),
        INCORRECT_SOLUTION: gettext('Provided solution incorrect'),
    },
    __all__: {
        LOCKED_ACCOUNT: gettext('This account has been locked'),
        SUSPENDED_ACCOUNT: gettext('This account has been suspended'),
        CREDENTIALS_MUST_CHANGE: gettext('This password has expired'),
        FORBIDDEN: gettext('Login failed'),
        LOGIN_GATED: STANDARD_SERVER_ERROR,
    },
};

export const LOOKUP_ERROR_SPEC = {
    USER_NOT_EXISTS: {
        email: NOT_EMAIL_ASSOCIATED,
    },
    PASSWORD_INCORRECT: {
        password: PASSWORD_INCORRECT,
    },
    EMAIL_INVALID: {
        email: ENTER_VALID_EMAIL,
    },
};

/* istanbul ignore next */
export const ACCOUNT_STATUS_ERROR_SPEC = {
    default: () => STANDARD_SERVER_ERROR,
};

/* istanbul ignore next */
export const SET_PASSWORD_ERROR_SPEC = {
    default: (statusCode, error) => {
        let message = STANDARD_SERVER_ERROR;

        if (statusCode === 400 && error && error.error_description) {
            message = error.error_description;
        }

        return message;
    },
};

const NOT_AUTHORIZED_ERROR = gettext(
    'You do not have permission to access the resource you requested.',
);

const SUBSCRIPTION_NOT_FOUND = gettext(
    'The profile you requested does not exist.',
);

/* istanbul ignore next */
const GENERAL_ERROR_SPEC = {
    NOT_AUTHORIZED: () => NOT_AUTHORIZED_ERROR,
    SUBSCRIPTION_NOT_FOUND: () => SUBSCRIPTION_NOT_FOUND,
    USER_HAS_NO_PASSWORD: () => 'USER_HAS_NO_PASSWORD',

    default: () => STANDARD_SERVER_ERROR,
};

/* istanbul ignore next */
const EXTERNAL_IDENTITY_ERROR_SPEC = {
    [SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK]: {
        EXTERNAL_USER_ID_ALREADY_USED: () =>
            gettext(
                'This Facebook profile is already connected to an Eventbrite account. Try using a different Facebook profile.',
            ),
    },
};

/**
 * This calls the social_action endpoint which is responsible for:
 * 1) setting data into redis
 * 2) trying to login the user
 * 3) returning a logged user
 * 4) returning that the user has another account and cannot be logged
 */
/**
 *
 * @param {{sourceProvider: string, accessToken: string}} data
 */
export const socialLogIn = (data) =>
    fetchAuthentication({}, SOCIAL_ACTION_URL, {
        method: 'POST',
        body: formatQuery(data),
    });

export const lookupUser = (email, socialProvider = '', sub = '') =>
    sdkRequest('/users/lookup/', {
        method: 'POST',
        body: JSON.stringify({
            email,
            source_user_id: sub,
            source_provider: socialProvider,
        }),
    }).catch(translateServerErrors(ACCOUNT_STATUS_ERROR_SPEC));

export const setPassword = (data) =>
    fetchJSON(SET_PASSWORD_URL, {
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
        },

        method: 'POST',
        body: formatQuery(data),
    }).catch(async (response) => {
        let body = {};

        try {
            body = await response.json();
        } catch (error) {
            body = {};
        }

        translateServerErrors(SET_PASSWORD_ERROR_SPEC)({
            parsedError: response.status,
            response: body,
        });
    });

export const setLinkedAccountApi = (internal_token) =>
    sdkRequest('/api/v3/auth/link_account_with_token/', {
        method: 'POST',
        body: JSON.stringify({
            internal_token: internal_token,
        }),
    });

export const postAddExternalIdentity = (
    userId = null,
    externalProvider,
    externalToken,
) =>
    sdkRequest(`/api/v3/users/${userId}/external_identities/`, {
        method: 'POST',
        body: JSON.stringify({
            is_data_stored:
                externalProvider == SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE,
            external_identity: {
                external_token: externalToken,
                external_provider: externalProvider,
            },
        }),
    })
        .catch(
            translateServerErrors({
                ...GENERAL_ERROR_SPEC,
                ...EXTERNAL_IDENTITY_ERROR_SPEC[externalProvider],
            }),
        )
        .then(
            ({
                id: externalIdentityId,
                external_user_id: externalUserId,
                external_provider: externalIdentityProvider,
            }) => ({
                externalIdentityId,
                externalUserId,
                externalIdentityProvider,
            }),
        );
